import { z } from 'zod';

import type { Address } from './party.js';

export const DELIVERY_STATUSES = ['pending', 'assigned', 'out_for_delivery', 'delivered', 'failed'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];
export const DeliveryStatusSchema = z.enum(DELIVERY_STATUSES);

/** A delivery / dispatch job — a sales order assigned to a driver and tracked to the door. */
export interface Delivery {
  id: string;
  docNumber: string;
  salesOrderId: string;
  sourceNumber: string;              // snapshot of the sales-order doc number
  partyId: string;
  status: DeliveryStatus;
  driverUserId: string | null;
  driverName: string | null;
  recipientName: string | null;
  phone: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  address: Address | null;           // full snapshot
  latitude: string | null;
  longitude: string | null;
  proofAttachmentId: string | null;  // proof-of-delivery photo (attachments.id)
  notes: string | null;
  failureReason: string | null;
  assignedAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  updatedAt: string;
  // joined order context (read-only, for the board):
  currencyCode: string | null;
  orderTotal: string | null;
}

/** A user eligible to be assigned as a driver (holds `delivery.deliver`). */
export interface DeliveryDriver {
  id: string;
  fullName: string;
  email: string;
}

export const DeliveryCreateFromOrderSchema = z.object({ salesOrderId: z.string().uuid() });
export type DeliveryCreateFromOrder = z.infer<typeof DeliveryCreateFromOrderSchema>;

export const DeliveryAssignSchema = z.object({ driverUserId: z.string().uuid().nullable() });
export type DeliveryAssign = z.infer<typeof DeliveryAssignSchema>;

export const DeliveryStatusUpdateSchema = z.object({
  status: DeliveryStatusSchema,
  failureReason: z.string().max(300).optional(),
  notes: z.string().max(500).optional(),
});
export type DeliveryStatusUpdate = z.infer<typeof DeliveryStatusUpdateSchema>;
